import {ColorSchemeType, Flag} from 'platform/components';
import {Box, HStack, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {always} from 'ramda';

import {AiLeadEmailMessageResponseBody, useGetLeadQualityOptionsQuery} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, parseDate} from 'shared/web';

import {MessageText} from './MessageText';

export interface EmailLeadPreviewDialogProps {
  message: AiLeadEmailMessageResponseBody;
}

export function EmailLeadPreviewDialog(props: EmailLeadPreviewDialogProps) {
  const formatDateTime = useDateTimeFormatter();

  const {data: leadQualityOptions} = useGetLeadQualityOptionsQuery();

  const aiLeadQuality = leadQualityOptions?.find(
    (option) => option.value === props.message.aiLeadQuality
  );

  const aiSentimentColor = match<string | null | undefined, ColorSchemeType>(
    props.message.aiSentiment
  )
    .with('positive', always('green'))
    .with('negative', always('red'))
    .otherwise(always('neutral'));

  const aiSentimentLabel = match<string | null | undefined, string>(props.message.aiSentiment)
    .with('positive', always('entity.interest.aiLead.sentiment.positive'))
    .with('negative', always('entity.interest.aiLead.sentiment.negative'))
    .otherwise(always('entity.interest.aiLead.sentiment.neutral'));

  return (
    <HStack spacing={4}>
      <Box
        flexGrow={1}
        minHeight={200}
        width="70%"
        backgroundColor="palettes.neutral.20.100"
        borderRadius="medium"
        padding={4}
      >
        <HStack justify="center" align="center">
          <Box height="100%" padding={10} backgroundColor="general.white">
            <MessageText htmlData={props.message.body} />
          </Box>
        </HStack>
      </Box>

      <Box
        flexGrow={1}
        width="30%"
        backgroundColor="palettes.neutral.20.100"
        borderRadius="medium"
        padding={4}
      >
        <VStack spacing={4}>
          <VStack spacing={1}>
            <Text size="xSmall" color="secondary">
              {i18n.t('entity.interest.aiLead.subject')}
            </Text>
            <Text size="small">{props.message.subject}</Text>
          </VStack>
          <VStack spacing={1}>
            <Text size="xSmall" color="secondary">
              {i18n.t('entity.interest.aiLead.recipient')}
            </Text>
            <Text size="small">{props.message.from}</Text>
          </VStack>

          <VStack spacing={1}>
            <Text size="xSmall" color="secondary">
              {i18n.t('entity.interest.aiLead.sent')}
            </Text>
            <Text size="small">
              {formatDateTime('dateTimeShort', parseDate(props.message.receivedAt))}
            </Text>
          </VStack>

          <HStack width="100%">
            <Box flex={1}>
              <VStack spacing={1}>
                <Text size="xSmall" color="secondary">
                  {i18n.t('entity.interest.aiLead.leadLevel')}
                </Text>
                <Text size="small">
                  <Flag
                    colorScheme={(aiLeadQuality?.color as ColorSchemeType) ?? 'neutral'}
                    label={aiLeadQuality?.label ?? 'Neutral'}
                  />
                </Text>
              </VStack>
            </Box>
            <Box flex={1}>
              <VStack spacing={1}>
                <Text size="xSmall" color="secondary">
                  {i18n.t('entity.interest.aiLead.sentiment')}
                </Text>
                <Text size="small">
                  <Flag colorScheme={aiSentimentColor} label={i18n.t(aiSentimentLabel)} />
                </Text>
              </VStack>
            </Box>
          </HStack>

          <VStack spacing={1}>
            <Text size="xSmall" color="secondary">
              {i18n.t('entity.interest.aiLead.emailSummary')}
            </Text>
            <Text size="small">{props.message.aiSummary ?? EMPTY_PLACEHOLDER}</Text>
          </VStack>
        </VStack>
      </Box>
    </HStack>
  );
}
